import { useEffect, useMemo, useState } from "react";
import { Pencil, Save, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { sortMixes } from "@/lib/mixSort";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/**
 * QuickEditDocketPopover — a compact inline editor for a single docket.
 *
 * Props:
 *   docket     : full docket object (needs id, quantity_m3, price_per_m3,
 *                mix_code, extra_tag_ids, wait_charge)
 *   mixes      : array of mix templates (id, code, name)
 *   extraTags  : array of active extra_tags (id, name, price_per_m3, color)
 *   onSaved    : (updatedDocket) => void
 *   testIdSuffix: string appended to data-testid (usually docket_no)
 *   size       : "sm" | "md" (chip size)
 */
export default function QuickEditDocketPopover({
  docket,
  mixes = [],
  extraTags = [],
  onSaved,
  testIdSuffix = "",
  size = "sm",
  asDialog = false,          // render as a centred modal (Dockets page) instead of an inline popover
  open: openProp,
  onOpenChange,
  onAdvanced,                // optional: "Advanced edit" link handler (dialog mode)
}) {
  const [openState, setOpenState] = useState(false);
  const open = asDialog ? !!openProp : openState;
  const setOpen = (v) => { if (asDialog) onOpenChange?.(v); else setOpenState(v); };
  const [qty, setQty] = useState(String(docket.quantity_m3 ?? ""));
  const [rate, setRate] = useState(String(docket.price_per_m3 ?? ""));
  const [mixCode, setMixCode] = useState(docket.mix_code ?? "");
  const [extras, setExtras] = useState(new Set(docket.extra_tag_ids || []));
  const [wait, setWait] = useState(String(docket.wait_charge ?? 0));
  const [po, setPo] = useState(docket.po_number ?? "");
  const [poApplyAll, setPoApplyAll] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setQty(String(docket.quantity_m3 ?? ""));
      setRate(String(docket.price_per_m3 ?? ""));
      setMixCode(docket.mix_code ?? "");
      setExtras(new Set(docket.extra_tag_ids || []));
      setWait(String(docket.wait_charge ?? 0));
    }
  }, [open, docket]);

  const sortedMixes = useMemo(() => sortMixes(mixes || []), [mixes]);

  const toggleExtra = (id) => {
    const next = new Set(extras);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExtras(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      const nQty = parseFloat(qty);
      const nRate = parseFloat(rate);
      const nWait = parseFloat(wait);
      if (!Number.isNaN(nQty) && nQty !== Number(docket.quantity_m3)) payload.quantity_m3 = nQty;
      if (!Number.isNaN(nRate) && nRate !== Number(docket.price_per_m3)) payload.price_per_m3 = nRate;
      if (mixCode && mixCode !== docket.mix_code) payload.mix_code = mixCode;
      const oldExtras = new Set(docket.extra_tag_ids || []);
      const changed = extras.size !== oldExtras.size || [...extras].some((x) => !oldExtras.has(x));
      if (changed) payload.extra_tag_ids = [...extras];
      if (!Number.isNaN(nWait) && nWait !== Number(docket.wait_charge || 0)) {
        payload.wait_charge = nWait;
        if (nWait < Number(docket.wait_charge || 0)) {
          const reason = window.prompt(`Wait charge reduced from $${Number(docket.wait_charge || 0).toFixed(2)} to $${nWait.toFixed(2)}.\nReason (required — shown to Accounts):`, "");
          if (reason === null) { setSaving(false); return; }
          if (!reason.trim()) { toast.error("A reason is required to remove or reduce waiting time"); setSaving(false); return; }
          payload.wait_waive_reason = reason.trim();
        }
      }

      const newPo = po.trim().toUpperCase();
      if (newPo !== (docket.po_number || "").trim().toUpperCase()) {
        payload.po_number = newPo;
        payload.po_apply_to_order = poApplyAll;
      }

      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setOpen(false);
        return;
      }
      const res = await api.patch(`/dockets/${docket.id}`, payload);
      const c = res.data?.po_cascade;
      if (c && (c.dockets > 1 || c.invoices > 0)) {
        toast.success(`Docket ${docket.docket_no || ""} updated`, {
          description: `PO applied to ${c.dockets} load${c.dockets === 1 ? "" : "s"} on this order${c.invoices ? ` and ${c.invoices} invoice${c.invoices === 1 ? "" : "s"}` : ""}.`,
        });
      } else {
        toast.success(`Docket ${docket.docket_no || ""} updated`);
      }
      setOpen(false);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update docket");
    } finally {
      setSaving(false);
    }
  };

  const total = useMemo(() => {
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    const extrasTotal = [...extras].reduce((sum, id) => {
      const t = extraTags.find((e) => e.id === id);
      return sum + (t ? Number(t.price_per_m3 || 0) * q : 0);
    }, 0);
    const w = parseFloat(wait) || 0;
    return q * r + extrasTotal + w;
  }, [qty, rate, extras, wait, extraTags]);

  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  const lg = asDialog;
  const inputCls = lg ? "rounded-none bg-transparent border-industrial mono h-10 text-sm" : "rounded-none bg-transparent border-industrial mono h-8 text-xs";
  const lblCls = lg ? "label-tag text-[11px] mb-1" : "label-tag text-[9px] mb-0.5";

  const body = (
      <>
        <div className={`flex items-center justify-between ${lg ? "mb-4" : "mb-2"}`}>
          <div className={`mono ${lg ? "text-xs" : "text-[10px]"} uppercase tracking-widest text-muted-industrial`}>
            Quick edit · <span className="text-ink font-bold">{docket.docket_no}</span>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-muted-industrial hover:text-ink"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className={`grid grid-cols-2 ${lg ? "gap-4 mb-4" : "gap-2 mb-2"}`}>
          <label className="block">
            <div className={lblCls}>Qty (m³)</div>
            <Input
              data-testid="quick-edit-qty"
              type="number"
              step="0.1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <div className={lblCls}>Rate ($/m³)</div>
            <Input
              data-testid="quick-edit-rate"
              type="number"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className={inputCls}
            />
          </label>
        </div>

        <label className={`block ${lg ? "mb-4" : "mb-2"}`}>
          <div className={lblCls}>Mix</div>
          <select
            data-testid="quick-edit-mix"
            value={mixCode}
            onChange={(e) => setMixCode(e.target.value)}
            className={`w-full bg-transparent border border-industrial mono px-2 ${lg ? "h-10 text-sm" : "h-8 text-xs"}`}
          >
            {!sortedMixes.some((m) => m.code === mixCode) && mixCode && (
              <option value={mixCode}>{mixCode} (current)</option>
            )}
            {sortedMixes.map((m) => (
              <option key={m.id || m.code} value={m.code}>
                {m.code}{m.name ? ` · ${m.name}` : ""}
              </option>
            ))}
          </select>
        </label>

        <label className={`block ${lg ? "mb-4" : "mb-2"}`}>
          <div className={lblCls}>Wait charge ($)</div>
          <Input
            data-testid="quick-edit-wait"
            type="number"
            step="0.01"
            value={wait}
            onChange={(e) => setWait(e.target.value)}
            className={inputCls}
          />
        </label>

        {/* Customer PO — usually phoned through after the pour, and it belongs
            to the whole order rather than this one load. */}
        <div className={`border border-industrial ${lg ? "p-3 mb-4" : "p-2 mb-2"}`}>
          <div className={lblCls}>Customer PO No.</div>
          <Input
            data-testid="quick-edit-po"
            value={po}
            onChange={(e) => setPo(e.target.value)}
            placeholder="e.g. PO-4471"
            className={`${inputCls} uppercase`}
          />
          <label className={`flex items-center gap-2 cursor-pointer select-none ${lg ? "mt-2.5" : "mt-1.5"}`}>
            <input
              type="checkbox"
              data-testid="quick-edit-po-apply-all"
              checked={poApplyAll}
              onChange={(e) => setPoApplyAll(e.target.checked)}
              className="accent-[rgb(var(--primary))] w-4 h-4"
            />
            <span className={`${lg ? "text-xs" : "text-[10px]"} text-muted-industrial`}>Apply to every load on this order <span className="text-ink">and its invoice</span></span>
          </label>
        </div>

        {extraTags.length > 0 && (
          <div className={lg ? "mb-4" : "mb-2"}>
            <div className={lblCls}>Extras</div>
            <div className={`flex flex-wrap ${lg ? "gap-1.5" : "gap-1"}`}>
              {extraTags
                .filter((t) => t.active !== false)
                .map((t) => {
                  const on = extras.has(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`quick-edit-extra-${t.name}`}
                      onClick={() => toggleExtra(t.id)}
                      className={`mono ${lg ? "text-[11px] px-2 py-1" : "text-[9px] px-1.5 py-0.5"} uppercase tracking-wider border transition-colors ${on ? "bg-primary-orange/20 border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial hover:border-primary-orange/50"}`}
                      style={on ? {} : { borderColor: t.color ? `${t.color}55` : undefined }}
                    >
                      {t.name} · ${Number(t.price_per_m3 || 0).toFixed(2)}/m³
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        <div className={`flex items-center justify-between border-t border-industrial ${lg ? "pt-4 mt-2" : "pt-2 mt-1"}`}>
          <div className={`mono ${lg ? "text-xs" : "text-[10px]"} uppercase tracking-widest text-muted-industrial`}>
            Line total: <span className="text-primary-orange font-bold">${total.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-3">
            {lg && onAdvanced && (
              <button type="button" data-testid="quick-edit-advanced" onClick={() => { setOpen(false); onAdvanced(docket); }} className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-ink underline-offset-2 hover:underline">Advanced…</button>
            )}
            <button
              data-testid="quick-edit-save"
              type="button"
              onClick={save}
              disabled={saving}
              className={`mono uppercase tracking-widest border border-primary-orange text-primary-orange bg-primary-orange/10 hover:bg-primary-orange/20 disabled:opacity-40 inline-flex items-center gap-1 ${lg ? "text-xs px-4 py-2" : "text-[10px] px-2.5 py-1.5"}`}
            >
              <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </>
  );

  if (asDialog) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial p-6 w-[560px] max-w-[95vw] text-ink [&>button]:hidden" data-testid="quick-edit-dialog">
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`quick-edit-docket-${testIdSuffix}`}
          type="button"
          onClick={(e) => e.stopPropagation()}
          title="Quick edit docket"
          className="inline-flex items-center justify-center p-1 border border-industrial text-muted-industrial hover:text-primary-orange hover:border-primary-orange transition-colors"
        >
          <Pencil className={iconSize} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        className="w-[360px] bg-surface border-industrial p-4 rounded-none text-ink shadow-2xl z-[200]"
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}
